<template>
  <div class="validation-drawer-content">
    <!-- Header with configuration button" -->
    <h3 class="title is-5 mb-2">Document Checkup</h3>
    
    <!-- Configure button between Document Checkup and Validation Rules -->
     <!-- TODO: UNCOMMENT BELOW WHEN WE WANT TO ENABLE USER CONFIGURABLE CHECKUP -->
    <!-- <div class="mb-3">
      <button 
        class="button is-primary is-small" 
        @click="showConfigModal = true"
      >
        <span class="icon">
          <i class="fas fa-cog"></i>
        </span>
        <span>Configure Checkup</span>
      </button>
    </div> -->

    <!-- Validation Rules List -->
    <!-- <div class="validation-rules-list">
      <div
        v-for="rule in validationRules"
        :key="rule.id"
        class="box validation-box"
        :class="rule.passed ? 'has-background-success-light' : 'has-background-danger-light'"
        :id="'validation-rule-' + rule.id"
      > -->
      <div
        v-for="(rules, docType) in groupedRules"
        :key="docType"
        class="doc-group"
      >
        <!-- Section header -->
        <div
          class="doc-header level is-clickable"
          @click="toggleSection(docType)"
        >
          <div class="level-left">
            <div class="level-item">
              <!-- Use a right-chevron that rotates on open -->
              <i
                :class="['fas', 'fa-chevron-right', { 'rotate-90': openSections[docType] }]"
                class="icon-toggle"
              ></i>
          <span class="doc-title">{{ docType }}</span>
        </div>
      </div>
    </div>

      <transition name="fade">
        <div v-if="openSections[docType]" class="validation-rules-list">
          <small class="pass-count">
            {{ rules.filter(r=>r.passed).length }} of {{ rules.length }} checks passed
          </small>
          <div
            v-for="rule in rules"
            :key="rule.id"
            class="box validation-box"
            :class="rule.passed ? 'has-background-success-light' : 'has-background-danger-light'"
          >
        <!-- Rule header with status indicator -->
        <div class="validation-header">
          <div class="rule-title-container">
            <h4 class="rule-description">{{ rule.description }}</h4>
            <h5 class="rule-name">{{ rule.name }}</h5>
          </div>
          <span 
            class="tag status-tag" 
            :class="rule.passed ? 'is-success' : 'is-danger'"
          >
            {{ rule.passed ? 'in order' : 'needs attention' }}
          </span>
        </div>

        <!-- Rule details -->
        <div class="validation-details">
          <p v-if="!rule.passed" class="details-text has-text-danger-dark">
            <strong>Issues:</strong> {{ cleanDetails(rule.details) }}
          </p>
          <p v-else-if="rule.passed && rule.details" class="details-text has-text-success-dark">
            <strong>Details:</strong> {{ rule.details }}
          </p>
          <p v-else-if="rule.passed" class="details-text has-text-success-dark">
            All validation checks have passed.
          </p>
        <!-- Ignore button with loading state -->
        <div class="mt-2">
          <!-- <o-tooltip
            variant="success"
            position="bottom"
            :active="true"
            label="Delete check result from document."
            multiline
            class="xs"
          > -->
            <button 
              class="button is-small is-outlined" 
              :class="rule.passed ? 'is-success' : 'is-danger'"
              @click="ignoreRule(rule.id)"
              :disabled="deletingRules.includes(rule.id)"
            >
            <span class="icon is-small">
              <i :class="deletingRules.includes(rule.id) ? 'fas fa-spinner fa-spin' : 'fas fa-times'"></i>
            </span>
            <span>{{ deletingRules.includes(rule.id) ? 'Deleting...' : 'Ignore This Check' }}</span>
          </button>
        <!-- </o-tooltip> -->
        </div>
      </div>
    </div>
  </div>
    </transition>
  </div>

    <!-- Empty state when no validation rules -->
    <div v-if="validationRules.length === 0" class="notification is-info">
      <!-- No checks available for this document type -->
      <p v-if="!hasAvailableChecks">
        No CheckUp rules configured for this document type.
      </p>
      <!-- Checks are available but none active -->
      <p v-else>
        All Doc Checkup data has been deleted for this document. Please re-run validation checks to populate new Doc Checkup results.
      </p>
    </div>
    
    <!-- Configuration Modal -->
    <div class="modal" :class="{ 'is-active': showConfigModal }">
      <div class="modal-background" @click="showConfigModal = false"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">Configure Validation Checks</p>
          <button 
            class="delete" 
            aria-label="close" 
            @click="showConfigModal = false"
          ></button>
        </header>
        <section class="modal-card-body">
          <!-- Tabs for managing existing vs creating new -->
          <div class="tabs">
            <ul>
              <li :class="{ 'is-active': activeTab === 'manage' }">
                <a @click="activeTab = 'manage'">Manage Checks</a>
              </li>
              <li :class="{ 'is-active': activeTab === 'create' }">
                <a @click="activeTab = 'create'">Create New Check</a>
              </li>
            </ul>
          </div>
          
          <!-- Manage existing checks -->
          <div v-if="activeTab === 'manage'" class="config-tab-content">
            <div v-if="isLoading" class="has-text-centered py-5">
              <span class="icon is-large">
                <i class="fas fa-spinner fa-pulse fa-2x"></i>
              </span>
              <p>Loading validation checks...</p>
            </div>
            
            <div v-else>
              <h4 class="subtitle is-6 mb-2">Default Checks</h4>
              <div 
                  v-for="(check, index) in availableChecks.default_checks" 
                  :key="`default-${check.id}-${index}`"
                  class="box p-3 mb-3"
                >
                <div class="level mb-0">
                  <div class="level-left">
                    <div>
                      <p class="mb-1"><strong>{{ check.name }}</strong></p>
                      <p class="is-size-7">{{ check.description }}</p>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="field">
                      <input 
                        type="checkbox" 
                        :id="'check-' + check.id" 
                        :checked="check.is_enabled"
                        @change="toggleCheck(check)"
                      >
                      <label :for="'check-' + check.id">{{ check.is_enabled ? 'Enabled' : 'Disabled' }}</label>
                    </div>
                  </div>
                </div>
              </div>
              
              <h4 class="subtitle is-6 mb-2 mt-4">Custom Checks</h4>
              <div v-if="availableChecks.custom_checks.length === 0" class="notification is-light">
                <p>You haven't created any custom checks yet.</p>
              </div>
              <div 
                v-for="(check, index) in availableChecks.custom_checks" 
                :key="`custom-${check.id}-${index}`"
                class="box p-3 mb-3"
              >
                <div class="level mb-0">
                  <div class="level-left">
                    <div>
                      <p class="mb-1"><strong>{{ check.name }}</strong></p>
                      <p class="is-size-7">{{ check.description }}</p>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="buttons">
                      <button 
                        class="button is-small is-info mr-1"
                        @click="viewEditCheck(check)"
                      >
                        <span class="icon">
                          <i class="fas fa-edit"></i>
                        </span>
                      </button>
                      <button 
                        class="button is-small is-danger"
                        @click="deleteCheck(check)"
                      >
                        <span class="icon">
                          <i class="fas fa-trash"></i>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
          
            </div>
          </div>
          <!-- Add a new Edit Check Modal -->
          <div class="modal" :class="{ 'is-active': showEditModal }">
            <div class="modal-background" @click="showEditModal = false"></div>
            <div class="modal-card">
              <header class="modal-card-head">
                <p class="modal-card-title">Edit Validation Check</p>
                <button 
                  class="delete" 
                  aria-label="close" 
                  @click="showEditModal = false"
                ></button>
              </header>
              <section class="modal-card-body" v-if="editingCheck">
                <div v-if="isLoadingCheck" class="has-text-centered py-5">
                  <span class="icon is-large">
                    <i class="fas fa-spinner fa-pulse fa-2x"></i>
                  </span>
                  <p>Loading check details...</p>
                </div>
                
                <div v-else>
                  <div class="field">
                    <label class="label">Name</label>
                    <div class="control">
                      <input 
                        class="input" 
                        type="text" 
                        v-model="editingCheck.name"
                        placeholder="e.g., signature_validation"
                      >
                    </div>
                  </div>
                  
                  <div class="field">
                    <label class="label">Question</label>
                    <div class="control">
                      <input 
                        class="input" 
                        type="text" 
                        v-model="editingCheck.question"
                        placeholder="e.g., Is the document properly signed?"
                      >
                    </div>
                  </div>
                  
                  <div class="field">
                    <label class="label">Description</label>
                    <div class="control">
                      <input 
                        class="input" 
                        type="text" 
                        v-model="editingCheck.description"
                        placeholder="e.g., Check if the document has valid signatures"
                      >
                    </div>
                  </div>
                  
                  <div class="field">
                    <label class="label">Prompt Details</label>
                    <div class="control">
                      <textarea 
                        class="textarea" 
                        v-model="editingCheck.prompt_details"
                        placeholder="e.g., Look for signature fields in the document and verify if they contain signatures."
                      ></textarea>
                    </div>
                  </div>
                  
                  <div class="field">
                    <label class="label">Concept Dependencies</label>
                    <div v-for="(dep, index) in editingCheck.concept_dependencies" :key="`edit-dep-${index}`" class="box p-3 mb-2">
                      <div class="columns">
                        <div class="column">
                          <input 
                            class="input" 
                            type="text" 
                            v-model="dep.concept"
                            placeholder="e.g., signature"
                          >
                        </div>
                        <div class="column">
                          <input 
                            class="input" 
                            type="text" 
                            v-model="dep.description"
                            placeholder="e.g., A signature on the document"
                          >
                        </div>
                        <div class="column is-narrow">
                          <button 
                            class="button is-danger"
                            @click="removeEditDependency(index)"
                          >
                            <span class="icon">
                              <i class="fas fa-times"></i>
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                    <button 
                      class="button is-small is-info mt-2"
                      @click="addEditDependency"
                    >
                      <span class="icon">
                        <i class="fas fa-plus"></i>
                      </span>
                      <span>Add Dependency</span>
                    </button>
                  </div>
                </div>
              </section>
              <footer class="modal-card-foot">
                <button 
                  class="button is-primary" 
                  :disabled="!isEditCheckValid || isUpdating"
                  @click="updateCheck"
                >
                  <span v-if="isUpdating" class="icon">
                    <i class="fas fa-spinner fa-pulse"></i>
                  </span>
                  <span>Update Check</span>
                </button>
                <button 
                  class="button" 
                  @click="showEditModal = false"
                >
                  Cancel
                </button>
              </footer>
            </div>
          </div>
          <!-- Create new check -->
          <div v-if="activeTab === 'create'" class="config-tab-content">
            <div class="field">
              <label class="label">Name</label>
              <div class="control">
                <input 
                  class="input" 
                  type="text" 
                  v-model="newCheck.name"
                  placeholder="e.g., policy_coverage_adequacy"
                >
              </div>
              <p class="help">A unique identifier for this check</p>
            </div>
            
            <div class="field">
              <label class="label">Question</label>
              <div class="control">
                <input 
                  class="input" 
                  type="text" 
                  v-model="newCheck.question"
                  placeholder="e.g., Does the insurance policy provide adequate coverage?"
                >
              </div>
              <p class="help">This will appear as the description in validation results</p>
            </div>
            
            <div class="field">
              <label class="label">Description</label>
              <div class="control">
                <input 
                  class="input" 
                  type="text" 
                  v-model="newCheck.description"
                  placeholder="e.g., Evaluates if insurance coverage meets standard adequacy thresholds"
                >
              </div>
              <p class="help">A short description of what this check does</p>
            </div>
            
            <div class="field">
              <label class="label">Prompt Details</label>
              <div class="control">
                <textarea 
                  class="textarea" 
                  v-model="newCheck.prompt_details"
                  placeholder="e.g., For insurance policies, assess if coverage limits meet standard recommendations for the asset or liability type. For home insurance, check if coverage matches home value. For auto insurance, verify liability limits meet state minimums."
                ></textarea>
              </div>
              <p class="help">Detailed instructions for the AI on how to perform this check</p>
            </div>
            
            <div class="field">
              <label class="label">Concept Dependencies</label>
              <div v-for="(dep, index) in newCheck.concept_dependencies" :key="index" class="box p-3 mb-2">
                <div class="columns">
                  <div class="column">
                    <input 
                      class="input" 
                      type="text" 
                      v-model="dep.concept"
                      placeholder="e.g., coverage limits"
                    >
                    <p class="help">The field or concept to check</p>
                  </div>
                  <div class="column">
                    <input 
                      class="input" 
                      type="text" 
                      v-model="dep.description"
                      placeholder="e.g., Maximum coverage amounts for different categories"
                    >
                    <p class="help">Description of what to look for</p>
                  </div>
                  <div class="column is-narrow">
                    <button 
                      class="button is-danger"
                      @click="removeDependency(index)"
                    >
                      <span class="icon">
                        <i class="fas fa-times"></i>
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              <button 
                class="button is-small is-info mt-2"
                @click="addDependency"
              >
                <span class="icon">
                  <i class="fas fa-plus"></i>
                </span>
                <span>Add Dependency</span>
              </button>
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button 
            v-if="activeTab === 'create'"
            class="button is-primary" 
            :disabled="!isNewCheckValid"
            @click="createCheck"
          >
            Create Check
          </button>
          <button 
            class="button" 
            @click="showConfigModal = false"
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ValidationDetails',
  props: {
    validationRules: {
      type: Array,
      required: true,
    },
    documentType: {
      type: String,
      default: '',
    },
    fullClassification: {
      type: Array,
      default: () => ([]),
    },
    documentId: {
      type: String,
      default: '',
      required: true
    }
  },
  data() {
    return {
      showConfigModal: false,
      activeTab: 'manage',
      isLoading: false,
      showEditModal: false,
      editingCheck: null,
      isLoadingCheck: false,
      isUpdating: false,
      deletingRules: [], // Track rules being deleted for UI feedback
      openSections: {},
      availableChecks: {
        document_type: '',
        default_checks: [],
        custom_checks: []
      },
      newCheck: {
        name: '',
        question: '',
        description: '',
        prompt_details: '',
        concept_dependencies: [
          { concept: '', description: '' }
        ]
      }
    };
  },
  mounted() {
    // This will load the checks when the component is first created
    this.loadValidationChecks();
  },
  computed: {
    groupedRules() {
      const classifications = this.fullClassification || [];

      // If multiple document‐types → use "Type (start–end)" or "Type (index)" keys
      if (classifications.length > 1) {
        const map = {};
        const indexCounters = {};

        classifications.forEach((c, idx) => {
          const base = c.classification || c.document_type || "Unclassified";
          let key;
          const startpage = c.start_page || c.page_start || null;
          const endpage = c.end_page || c.page_end || null

          // both pages present?
          if (startpage != null && endpage != null) {
            key = `${base} (${startpage} – ${endpage})`;
          } else {
            // missing pages → give each same-type a unique numeric suffix
            indexCounters[base] = (indexCounters[base] || 0) + 1;
            key = `${base} (${indexCounters[base]})`;
          }

          // collect rules matching this classification AND overlapping page ranges
          map[key] = this.validationRules.filter(rule => {
            const dt = rule.document_type
                    || this.documentType
                    || "Unclassified";
            // Must match document type
            const typeMatches = dt === c.classification  || c.document_type;
            
            // Must have overlapping page ranges
            let pageOverlaps = true;
            if (startpage != null && endpage != null && 
                rule.start_page != null && rule.end_page != null) {
              // Check if the rule's page range overlaps with classification's page range
              pageOverlaps = rule.start_page <= endpage && rule.end_page >= startpage;
            }
            
            return typeMatches && pageOverlaps;
          });
        });

        return map;
      }

      // Single (or zero) doc‐type → group by plain document_type
      return this.validationRules.reduce((acc, rule) => {
        const dt = rule.document_type
                || this.documentType
                || "Unclassified";
        if (!acc[dt]) acc[dt] = [];
        acc[dt].push(rule);
        return acc;
      }, {});
    },
    hasAvailableChecks() {
      return this.availableChecks &&
            ((this.availableChecks.default_checks && this.availableChecks.default_checks.length > 0) ||
              (this.availableChecks.custom_checks && this.availableChecks.custom_checks.length > 0));
    },
    passedValidations() {
      return this.validationRules.filter((rule) => rule.passed);
    },
    failedValidations() {
      return this.validationRules.filter((rule) => !rule.passed);
    },
    isNewCheckValid() {
      // Basic validation for new check form
      return (
        this.newCheck.name.trim() !== '' &&
        this.newCheck.question.trim() !== '' &&
        this.newCheck.description.trim() !== '' &&
        this.newCheck.prompt_details.trim() !== '' &&
        this.newCheck.concept_dependencies.length > 0 &&
        this.newCheck.concept_dependencies.every(dep => 
          dep.concept.trim() !== '' && dep.description.trim() !== ''
        )
      );
    }, 
    isEditCheckValid() {
    if (!this.editingCheck) return false;
    
    return (
      this.editingCheck.name.trim() !== '' &&
      this.editingCheck.question.trim() !== '' &&
      this.editingCheck.description.trim() !== '' &&
      this.editingCheck.prompt_details.trim() !== '' &&
      this.editingCheck.concept_dependencies.length > 0 &&
      this.editingCheck.concept_dependencies.every(dep => 
        dep.concept.trim() !== '' && dep.description.trim() !== ''
      )
    );
    },
  },
  created() {
    // default open any that have failures
    Object.entries(this.groupedRules).forEach(([docType, rules]) => {
      this.$set(this.openSections, docType, rules.some(r => !r.passed));
    });
  },
  methods: {
    toggleSection(docType) {
      this.openSections[docType] = !this.openSections[docType];
    },
    ensureChecksLoaded() {
      // Only load if we haven't already
      if (!this.availableChecks.default_checks.length && !this.availableChecks.custom_checks.length) {
        this.loadValidationChecks();
      }
    },

    cleanDetails(details) {
      // Check if details start with "errors:" and remove it
      if (details && details.startsWith('errors:')) {
        return details.replace('errors:', '').trim();
      }
      return details; // Return as-is if no match
    },
    // delete a validation check that is saved on the docupload object
    async ignoreRule(ruleId) {
      try {
        // Find the rule by ID to get its name
        const ruleToDelete = this.validationRules.find(rule => rule.id === ruleId);
        if (!ruleToDelete) {
          throw new Error('Rule not found');
        }
        
        // Add rule ID to deletingRules array to show loading state
        this.deletingRules.push(ruleId);
        
        // Call API to delete the rule
        const doc_type_delete = ruleToDelete.document_type || null
        await this.deleteRuleFromDatabase(ruleToDelete.name, doc_type_delete);
        
        // On success, remove the rule from local array
        this.validationRules = this.validationRules.filter(rule => rule.id !== ruleId);
        
        // Emit event for parent components
        this.$emit('validation-deleted');
      } catch (error) {
        // Handle error (maybe show a notification)
        console.error('Failed to delete validation rule:', error);
        alert(`Error deleting validation data: ${error.message}`);
      } finally {
        // Remove from deletingRules array regardless of outcome
        this.deletingRules = this.deletingRules.filter(id => id !== ruleId);
      }
    },
    
    // New method to call your API
    async deleteRuleFromDatabase(ruleName, document_type) {
      // Get the document ID from your component props or data
      const response = await fetch(`/api/v1/documents/${this.documentId}/delete-validation-check-data/`, {
        method: 'POST',
        headers: {
          'X-CSRFToken': this.$root.$data.csrfToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to_delete: {
            name: ruleName,
            document_type: document_type
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }
      
      return await response.json();
    },

    // Load validation checks when modal opens
    async loadValidationChecks() {
      this.isLoading = true;
      try {
        const response = await fetch(`/api/v1/documents/${this.documentId}/validation-checks/`, {
          method: 'GET',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to load validation check config');
        }
        
        this.availableChecks = await response.json();
      } catch (error) {
        console.error('Error loading validation check configuration for this doctype:', error);
        //alert(`Error loading DocCheckup checks: ${error.message}`);
      } finally {
        this.isLoading = false;
      }
    },
    
    // Toggle a check's enabled status
    async toggleCheck(check) {
      try {
        const response = await fetch(`/api/v1/documents/${this.documentId}/validation-checks/`, {
          method: 'PUT',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            check_id: check.id,
            is_enabled: !check.is_enabled
          })
        });
        
        if (!response.ok) {
          throw new Error('Failed to update check status');
        }
        
        // Update local state
        check.is_enabled = !check.is_enabled;
      } catch (error) {
        console.error('Error toggling check:', error);
        alert(`Failed to update check status: ${error.message}`);
      }
    },
    
    // Delete a custom check
    async deleteCheck(check) {
      if (!confirm(`Are you sure you want to delete the "${check.name}" check?`)) {
        return;
      }
      
      try {
        const response = await fetch(`/api/v1/documents/${this.documentId}/validation-checks/`, {
          method: 'DELETE',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ check_id: check.id })
        });
        
        if (!response.ok) {
          throw new Error('Failed to delete check');
        }
        
        // Remove from list
        this.availableChecks.custom_checks = this.availableChecks.custom_checks.filter(
          c => c.id !== check.id
        );
        
      } catch (error) {
        console.error('Error deleting check:', error);
        alert(`Failed to delete check: ${error.message}`);
      }
    },
    
    // Create a new validation check
    async createCheck() {
      try {
        const response = await fetch(`/api/v1/documents/${this.documentId}/validation-checks/`, {
          method: 'POST',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(this.newCheck)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create check');
        }
        
        // Reset form
        this.newCheck = {
          name: '',
          question: '',
          description: '',
          prompt_details: '',
          concept_dependencies: [{ concept: '', description: '' }]
        };
        
        // Switch to manage tab and reload checks
        this.activeTab = 'manage';
        await this.loadValidationChecks();
        
      } catch (error) {
        console.error('Error creating check:', error);
        alert(`Error creating check: ${error.message}`);
      }
    },
    async viewEditCheck(check) {
      this.isLoadingCheck = true;
      this.showEditModal = true;
      
      try {
        const response = await fetch(`/api/v1/documents/validation-checks/${check.id}/`, {
          method: 'GET',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to load check details');
        }
        
        this.editingCheck = await response.json();
      } catch (error) {
        console.error('Error loading check details:', error);
        alert(`Error loading check details: ${error.message}`);
        this.showEditModal = false;
      } finally {
        this.isLoadingCheck = false;
      }
    },
    
    addEditDependency() {
      if (this.editingCheck) {
        this.editingCheck.concept_dependencies.push({ concept: '', description: '' });
      }
    },
    
    removeEditDependency(index) {
      if (this.editingCheck && this.editingCheck.concept_dependencies.length > 1) {
        this.editingCheck.concept_dependencies.splice(index, 1);
      }
    },
    
    async updateCheck() {
      this.isUpdating = true;
      
      try {
        const response = await fetch(`/api/v1/documents/validation-checks/${this.editingCheck.id}/update/`, {
          method: 'PUT',
          headers: {
            'X-CSRFToken': this.$root.$data.csrfToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: this.editingCheck.name,
            description: this.editingCheck.description,
            question: this.editingCheck.question,
            prompt_details: this.editingCheck.prompt_details,
            concept_dependencies: this.editingCheck.concept_dependencies
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update check');
        }
        
        // Close modal and refresh list
        this.showEditModal = false;
        await this.loadValidationChecks();
        
      } catch (error) {
        console.error('Error updating check:', error);
        alert(`Error updating check: ${error.message}`);
      } finally {
        this.isUpdating = false;
      }
    },
    
    // Add a new dependency field
    addDependency() {
      this.newCheck.concept_dependencies.push({ concept: '', description: '' });
    },
    
    // Remove a dependency field
    removeDependency(index) {
      this.newCheck.concept_dependencies.splice(index, 1);
      
      // Always keep at least one dependency
      if (this.newCheck.concept_dependencies.length === 0) {
        this.addDependency();
      }
    },
  },
  watch: {
    showConfigModal(newVal) {
      if (newVal) {
        this.loadValidationChecks();
      }
    }
  }
};
</script>

<style scoped>
.icon-toggle {
  width: 1em;
  height: 1em;
  color: #6c6c6c;       /* match your brand grey */
  transition: transform .2s ease;
  margin-left: 0.25rem;
}
/* rotate when open */
.rotate-90 {
  transform: rotate(90deg);
}
/* make the header feel like a toggle, not a button */
.level.is-clickable {
  cursor: pointer;
  user-select: none;
}

.validation-box {
  margin-bottom: 1rem;
  transition: all 0.3s ease;
}

.validation-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.rule-title-container {
  flex: 1;
}

.rule-description {
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.rule-name {
  font-size: 0.8rem;
  color: #666;
}

.status-tag {
  margin-left: 1rem;
}

.validation-details {
  font-size: 0.9rem;
}

.config-tab-content {
  min-height: 300px;
}
</style>
